import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { SITE_CONFIG, getExchangeStatusLabel } from '@/config/site';

const PROJECT_FACTS = [
  { label: 'Token Symbol', value: '$BZX' },
  { label: 'Network', value: 'BNB Chain' },
  { label: 'Total Supply', value: '1,000,000,000' },
  { label: 'Exchange Status', value: getExchangeStatusLabel() },
];

export const Stats = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-50px' });

  return (
    <section
      id="stats-section"
      ref={ref}
      className="relative py-14 md:py-16 overflow-hidden"
      data-testid="stats-section"
    >
      {/* Premium gradient background */}
      <div className="absolute inset-0 bg-gradient-to-r from-[#0a0b0d] via-[#12141a] to-[#0a0b0d]" />

      {/* Gold accent lines */}
      <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-[#9C7941] to-transparent opacity-60" />
      <div className="absolute bottom-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-[#9C7941] to-transparent opacity-60" />

      {/* Animated glow orbs */}
      <motion.div
        animate={{ x: [0, 100, 0], opacity: [0.3, 0.5, 0.3] }}
        transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
        className="absolute top-1/2 left-1/4 -translate-y-1/2 w-64 h-32 bg-[#9C7941]/20 rounded-full blur-3xl"
      />
      <motion.div
        animate={{ x: [0, -100, 0], opacity: [0.3, 0.5, 0.3] }}
        transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut' }}
        className="absolute top-1/2 right-1/4 -translate-y-1/2 w-64 h-32 bg-[#EBD38D]/15 rounded-full blur-3xl"
      />

      <div className="max-w-7xl mx-auto px-6 md:px-10 xl:px-16 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="relative"
        >
          {/* Main stats container */}
          <div className="relative backdrop-blur-xl bg-gradient-to-r from-[#12141a]/80 via-[#1a1c22]/90 to-[#12141a]/80 rounded-3xl border border-[#9C7941]/20 overflow-hidden">

            {/* Inner glow effect */}
            <div className="absolute inset-0 bg-gradient-to-r from-[#9C7941]/5 via-transparent to-[#EBD38D]/5" />

            {/* Stats grid */}
            <div className="relative grid grid-cols-2 lg:grid-cols-4 divide-x divide-y lg:divide-y-0 divide-[#9C7941]/10">
              {PROJECT_FACTS.map((stat, index) => (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, y: 20 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="relative group"
                >
                  <div className="p-8 lg:p-10 text-center relative overflow-hidden">
                    {/* Hover glow */}
                    <div className="absolute inset-0 bg-gradient-to-b from-[#9C7941]/0 to-[#9C7941]/0 group-hover:from-[#9C7941]/10 group-hover:to-transparent transition-all duration-500" />

                    {/* Animated top border on hover */}
                    <div className="absolute top-0 left-1/2 -translate-x-1/2 w-0 h-[2px] bg-gradient-to-r from-[#9C7941] to-[#EBD38D] group-hover:w-full transition-all duration-500" />

                    {/* Content */}
                    <div className="relative">
                      <p className="text-[#8A8B90] text-xs font-medium uppercase tracking-[0.2em] mb-3">
                        {stat.label}
                      </p>

                      <div className="relative inline-block max-w-full">
                        <span className="text-2xl lg:text-3xl font-bold text-white tracking-tight break-words">
                          {stat.value}
                        </span>
                        {/* Subtle glow behind value */}
                        <div className="absolute inset-0 blur-xl bg-[#9C7941]/20 -z-10 scale-150 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Decorative corner elements */}
          <div className="absolute -top-1 -left-1 w-8 h-8 border-t-2 border-l-2 border-[#9C7941] rounded-tl-xl opacity-60" />
          <div className="absolute -top-1 -right-1 w-8 h-8 border-t-2 border-r-2 border-[#9C7941] rounded-tr-xl opacity-60" />
          <div className="absolute -bottom-1 -left-1 w-8 h-8 border-b-2 border-l-2 border-[#9C7941] rounded-bl-xl opacity-60" />
          <div className="absolute -bottom-1 -right-1 w-8 h-8 border-b-2 border-r-2 border-[#9C7941] rounded-br-xl opacity-60" />
        </motion.div>
      </div>
    </section>
  );
};

export default Stats;
